import * as React from 'react'

import { CheckBox, Table, Text, useTableDensity } from '@factorialco/deprecated-design-system'

import BodyWithInfiniteScroll from 'modules/core/components/DataTable/components/BodyWithInfiniteScroll'
import { selectableColumnWidth } from 'modules/core/components/DataTable/constants'
import { Selectable } from 'modules/core/components/DataTable/core/types'
import useBody from 'modules/core/components/DataTable/hooks/useBody'
import useData from 'modules/core/components/DataTable/hooks/useData'
import usePagination from 'modules/core/components/DataTable/hooks/usePagination'
import useSelectedRows from 'modules/core/components/DataTable/hooks/useSelectedRows'
import useSticky from 'modules/core/components/DataTable/hooks/useSticky'
import type { HeadCell, Row } from 'modules/core/components/DataTable/types'
import i18n from 'modules/core/lib/i18n'

type Content = string | React.ReactNode

interface BodyContentProps<T> {
  body: Row[]
  selectable?: Selectable
}

type BodyProps<T, S extends string> = {
  header: HeadCell<S>[]
  loadingMore: boolean
  selectable?: Selectable
}

function Content({ content }: { content: Content }) {
  if (typeof content === 'string' || typeof content === 'number') {
    return (
      <Text.Normal overflowWrap size="300">
        {content}
      </Text.Normal>
    )
  }

  if (React.isValidElement(content)) {
    return content
  }

  // TODO: ATM arrays are just joined strings ¯\_(ツ)_/¯
  // FIx this when we have a custom render solution
  if (Array.isArray(content)) {
    return <Text.Normal size="300">{content.join(', ')}</Text.Normal>
  }

  if (typeof content === 'boolean') {
    return (
      <Text.Normal size="300" capitalize>
        {content ? i18n.t('common.yes') : i18n.t('common.no')}
      </Text.Normal>
    )
  }

  if (content === undefined || content === null) {
    return null
  }

  throw new Error(
    `Invalid content type, it must be a string, array or a ReactNode. Content: ${content}`
  )
}

function SelectableBodyCell<T>({
  id,
  sticky,
  selectable = (item) => false,
  data,
}: {
  id: string
  sticky?: boolean
  selectable?: Selectable
  data: T
}) {
  const { selectedRows, onSelectRows, onUnselectRows } = useSelectedRows()
  const tableDensity = useTableDensity()
  const selected = selectedRows.has(String(id))
  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (selected) {
      onUnselectRows([String(id)])
    } else {
      onSelectRows([String(id)])
    }
  }

  const stickyProp = sticky ? ({ position: 'left' } as const) : undefined

  return (
    <Table.Cell width={selectableColumnWidth(tableDensity)} sticky={stickyProp}>
      <CheckBox onClick={onClick} checked={selected} disabled={!selectable(data)} />
    </Table.Cell>
  )
}

function BodyContent<T>({ body, selectable }: BodyContentProps<T>) {
  const getStickyProp = useSticky()
  const { data } = useData<T, ''>()

  return (
    <>
      {body.map(({ id, cells, to }, index) => {
        const rows = cells.map(({ id, content, colSpan, width, sticky }, index) => {
          const stickyProp = getStickyProp(index, cells.length, !!sticky, !!selectable)

          return (
            <Table.Cell key={id} colSpan={colSpan} width={width} sticky={stickyProp}>
              <Content content={content} />
            </Table.Cell>
          )
        })

        const selectCell = selectable && (
          <SelectableBodyCell
            id={String(id)}
            sticky={cells[0]?.sticky}
            selectable={selectable}
            data={data[index] as T}
          />
        )

        if (to) {
          return (
            <Table.Row.Router key={id} to={to}>
              {selectCell}
              {rows}
            </Table.Row.Router>
          )
        }

        return (
          <Table.Row key={id}>
            {selectCell}
            {rows}
          </Table.Row>
        )
      })}
    </>
  )
}

function Body<T, S extends string>({ loadingMore, header, selectable }: BodyProps<T, S>) {
  const { pagination } = usePagination()
  const body = useBody<T, S>(!!selectable)

  if (pagination.infiniteScroll) {
    return (
      <BodyWithInfiniteScroll
        header={header}
        loadingMore={loadingMore}
        paginationKind={pagination.kind}
        body={body}
        selectable={selectable}
      >
        <BodyContent body={body} selectable={selectable} />
      </BodyWithInfiniteScroll>
    )
  }

  return (
    <Table.Body>
      <BodyContent body={body} selectable={selectable} />
    </Table.Body>
  )
}

export default Body
